import {css, html, LitElement} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {state} from 'lit/decorators/state.js';

import {SetSimulatorEnvironmentEvent} from '../../../events/SimulatorEnvironmentEvents.js';
import {ShowSimulatorInstructionsEvent} from '../../../events/SimulatorInstructionsEvents.js';
import {SetSimulatorModeEvent} from '../../../events/SimulatorModeEvents.js';
import {SetSimulatorHandPhysicsEvent} from '../../../events/SimulatorPhysicsEvents.js';
import {ISimulatorSettingsPanelElement} from '../../../interfaces/ISimulatorSettingsPanelElement.js';
import {
  SimulatorEnvironment,
  SimulatorMode,
} from '../../../SimulatorOptions.js';

@customElement('xrblocks-simulator-settings')
export class SimulatorSettingsPanel
  extends LitElement
  implements ISimulatorSettingsPanelElement
{
  static styles = css`
    :host {
      position: fixed;
      bottom: 0;
      left: 0;
      z-index: 10000;
      font-family:
        system-ui,
        -apple-system,
        sans-serif;
    }

    .settings-btn {
      border: none;
      margin: 1rem;
      width: 3rem;
      height: 3rem;
      border-radius: 5rem;
      background: rgba(0, 0, 0, 0.5);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.2s ease;
    }

    .settings-btn:hover {
      background: rgba(0, 0, 0, 0.7);
    }

    .settings-btn svg {
      width: 1.5rem;
      height: 1.5rem;
      fill: currentColor;
      transition: transform 0.3s ease;
    }

    .settings-btn.open svg {
      transform: rotate(45deg);
    }

    .panel {
      position: absolute;
      bottom: 4.5rem;
      left: 1rem;
      background: rgba(0, 0, 0, 0.7);
      border: none;
      border-radius: 1.5rem;
      padding: 1.5rem;
      min-width: 16rem;
      color: #fff;
      opacity: 0;
      pointer-events: none;
      transform: translateY(10px);
      transition: all 0.3s ease;
      backdrop-filter: blur(8px);
    }

    .panel.open {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }

    h3 {
      margin: 0 0 1rem;
      font-size: 1.1rem;
      font-weight: 500;
      color: #fff;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      padding-bottom: 0.5rem;
    }

    .form-group {
      margin-bottom: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }

    .form-group:last-of-type {
      margin-bottom: 0;
    }

    label {
      font-size: 0.85rem;
      color: #ccc;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      cursor: pointer;
    }

    .checkbox-label:has(input:disabled) {
      cursor: not-allowed;
      opacity: 0.5;
    }

    input[type='checkbox'] {
      width: 1rem;
      height: 1rem;
      margin: 0;
      accent-color: #8ab4f8;
      cursor: inherit;
    }

    select {
      appearance: none;
      -webkit-appearance: none;
      background: rgba(0, 0, 0, 0.5)
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")
        no-repeat right 0.5rem center;
      background-size: 1rem;
      border: none;
      color: #fff;
      padding: 0.5rem 2.2rem 0.5rem 1.2rem;
      border-radius: 5rem;
      font-size: 0.9rem;
      outline: none;
      cursor: pointer;
    }

    select:hover,
    select:focus {
      background-color: rgba(0, 0, 0, 0.8);
    }

    option {
      background: #222;
      color: #fff;
    }

    .instructions-btn {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #fff;
      padding: 0.6rem 1.2rem;
      border-radius: 5rem;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }

    .instructions-btn:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.4);
    }

    .instructions-btn:active {
      background: rgba(255, 255, 255, 0.15);
    }
  `;

  @property({type: Array}) environments: SimulatorEnvironment[] = [];
  @property({type: Number}) activeEnvironmentIndex = 0;
  @property({type: String}) simulatorMode = SimulatorMode.USER;
  @property({type: Boolean}) instructionsEnabled = false;
  @property({type: Boolean}) handPhysicsAvailable = false;
  @property({type: Boolean}) handPhysicsEnabled = false;

  @state() private _isOpen = false;

  private _togglePanel() {
    this._isOpen = !this._isOpen;
  }

  private _onEnvironmentChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    const idx = parseInt(select.value, 10);
    this.activeEnvironmentIndex = idx;
    this.dispatchEvent(new SetSimulatorEnvironmentEvent(idx));
  }

  private _onModeChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    const newMode = select.value as SimulatorMode;
    this.simulatorMode = newMode;
    this.dispatchEvent(new SetSimulatorModeEvent(newMode));
  }

  private _onHandPhysicsChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.handPhysicsEnabled = input.checked;
    this.dispatchEvent(
      new SetSimulatorHandPhysicsEvent(this.handPhysicsEnabled)
    );
  }

  private _onShowInstructions() {
    this._isOpen = false;
    this.dispatchEvent(new ShowSimulatorInstructionsEvent());
  }

  render() {
    const modes = [
      {label: 'User', value: SimulatorMode.USER},
      {label: 'Navigation', value: SimulatorMode.POSE},
      {label: 'Hands', value: SimulatorMode.CONTROLLER},
      {label: 'Pointer Lock', value: SimulatorMode.POINTER_LOCK},
      {label: 'Editor', value: SimulatorMode.EDITOR},
    ];

    return html`
      <button
        class="settings-btn ${this._isOpen ? 'open' : ''}"
        @click=${this._togglePanel}
        title="Simulator Settings"
      >
        <svg viewBox="0 0 24 24">
          <path
            d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"
          />
        </svg>
      </button>

      <div class="panel ${this._isOpen ? 'open' : ''}">
        <h3>Simulator Settings</h3>

        <div class="form-group">
          <label>AR Simulation Environment</label>
          <select @change=${this._onEnvironmentChange}>
            ${this.environments.map(
              (env, idx) => html`
                <option
                  value=${idx}
                  ?selected=${idx === this.activeEnvironmentIndex}
                >
                  ${env.name ?? env.manifestPath.split('/').pop()}
                </option>
              `
            )}
          </select>
        </div>

        <div class="form-group">
          <label>Interaction Mode</label>
          <select @change=${this._onModeChange}>
            ${modes.map(
              (mode) => html`
                <option
                  value=${mode.value}
                  ?selected=${mode.value === this.simulatorMode}
                >
                  ${mode.label}
                </option>
              `
            )}
          </select>
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <span>Hand Physics</span>
            <input
              type="checkbox"
              .checked=${this.handPhysicsEnabled}
              ?disabled=${!this.handPhysicsAvailable}
              @change=${this._onHandPhysicsChange}
            />
          </label>
        </div>

        ${this.instructionsEnabled
          ? html`
              <div class="form-group">
                <button
                  class="instructions-btn"
                  @click=${this._onShowInstructions}
                >
                  View Simulator Instructions
                </button>
              </div>
            `
          : ''}
      </div>
    `;
  }
}
